import React from "react";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import StatusBadge from "./StatusBadge";
import {
  Copy,
  Check,
  ArrowDownToLine, 
  ArrowUpFromLine,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", key: "pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", key: "processing" },
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", key: "success" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", key: "failed" },
  expired: { icon: Clock, color: "bg-slate-100 text-slate-700", key: "expired" },
  cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", key: "cancelled" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", key: "declined" },
};

export default function TransactionDetailPanel({ transaction, open, onClose, defaultCurrency = "THB" }) {
  let t = null;
  let language = "en";
  try {
    const langContext = useLanguage();
    t = langContext?.t;
    language = langContext?.language || "en";
  } catch (e) {
    // Not wrapped in LanguageProvider
  }

  const translate = (key) => t ? t(key) : key;

  const fallbackNA = translate("nA") || "N/A";

  const getTransactionTypeLabel = (type) => {
    if (!type) return "";
    return type === "deposit" ? translate("deposit") : translate("withdrawal");
  };

  const [copied, setCopied] = React.useState(false);
  const [copiedRef, setCopiedRef] = React.useState(false);

  const formatDateSafe = (value, pattern = "MMM d, yyyy HH:mm:ss") => {
    if (!value) return fallbackNA;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallbackNA;
    return format(date, pattern, { locale: language === "th" ? th : enUS });
  };

  const formatAmountWithCurrency = (amount, currency) => {
    const num = Number(amount || 0);
    const currencyCode = String(currency || defaultCurrency || "THB").toUpperCase();
    const symbolByCurrency = { PKR: "Rs", IDR: "Rp" };
    const symbol = symbolByCurrency[currencyCode] || currencyCode;
    return `${symbol} ${num.toLocaleString()}`;
  };

  if (!transaction) return null;

  const effectiveCurrency = String(transaction?.currency || defaultCurrency || "THB").toUpperCase();

  const statusKey = (transaction?.status || '').toLowerCase();
  const status = statusConfig[statusKey] || statusConfig.pending;
  const StatusIcon = status.icon;

  const txStatus = String(transaction.status || "").toLowerCase();
  const isTerminal = ["success", "failed", "declined", "expired", "cancelled"].includes(txStatus);
  const isFailedTerminal = ["failed", "declined", "expired", "cancelled"].includes(txStatus);
  const isProcessingOrLater = ["processing", "pending", "in_process", ...["success", "failed", "declined", "expired", "cancelled"]].includes(txStatus);

  const statusTimeline = [
    {
      status: "created",
      label: translate("createdLabel") || "Created",
      time: transaction.createdAt,
      done: true,
    },
    {
      status: "processing",
      label: translate("processingLabel") || "Processing",
      time: transaction.processedAt ?? transaction.processed_at,
      done: isProcessingOrLater,
    },
    {
      status: isFailedTerminal ? (txStatus === "declined" ? "declined" : "failed") : "success",
      label: txStatus === "declined"
        ? (translate("declined") || "Declined")
        : isFailedTerminal
          ? (translate("failedLabel") || "Failed")
          : (translate("completedLabel") || "Completed"),
      time: transaction.completedAt ?? transaction.completed_at,
      done: isTerminal,
    },
  ];

  const toDisplayText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const errorMessageText = toDisplayText(
    transaction.error_message ?? transaction.errorMessage,
  );

  const callbackMessageText = toDisplayText(
    transaction.callback_response ??
      transaction.callbackResponse ??
      transaction.apiResponse,
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${transaction.type === 'deposit' ? 'bg-blue-100' : 'bg-amber-100'}`}>
              {transaction.type === 'deposit' ? (
                <ArrowDownToLine className="h-5 w-5 text-blue-600" />
              ) : (
                <ArrowUpFromLine className="h-5 w-5 text-amber-600" />
              )}
            </div>
            <div>
              <p className="text-lg font-bold">{getTransactionTypeLabel(transaction.type) || translate("transaction")}</p>
              <Badge className={`${status.color} gap-1 border-0`}>
                <StatusIcon className="h-3 w-3" />
                {translate(status.key)}
              </Badge>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Amount Section */}
          <div className="grid grid-cols-3 gap-2 py-4 bg-slate-50 rounded-xl px-4">
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">{translate("amount")}</p>
              <p className="text-lg font-bold text-slate-900 border-r border-slate-200">
                {formatAmountWithCurrency(transaction.amount, transaction.currency)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">{translate("fee")}</p>
              <p className="text-lg font-bold text-amber-700 border-r border-slate-200">
                {formatAmountWithCurrency(transaction.feeAmount ?? transaction.fee_amount, transaction.currency)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">{translate("netAmount")}</p>
              <p className="text-lg font-bold text-emerald-600">
                {formatAmountWithCurrency(transaction.netAmount ?? transaction.net_amount, transaction.currency)}
              </p>
            </div>
          </div>

          {/* Transaction IDs */}
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 mb-2">{translate("providerTransactionId")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm truncate">
                  {transaction.providerTransactionId ?? transaction.provider_transaction_id ?? fallbackNA}
                </code>
                <Button variant="outline" size="icon" onClick={() => {
                  navigator.clipboard.writeText(transaction.providerTransactionId ?? transaction.provider_transaction_id ?? "");
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}>
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <p className="text-sm text-slate-500 mb-2">{translate("systemId")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-xs truncate">
                  {transaction.id}
                </code>
                <Button variant="outline" size="icon" onClick={() => {
                  navigator.clipboard.writeText(transaction.id || "");
                  setCopiedRef(true);
                  setTimeout(() => setCopiedRef(false), 2000);
                }}>
                  {copiedRef ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("provider")}</p>
              <p className="text-sm font-medium">{transaction.providerCode ?? transaction.provider_code ?? fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("paymentMethod")}</p>
              <p className="text-sm font-medium capitalize">{(transaction.paymentMethod ?? transaction.payment_method)?.replace('_', ' ') || fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("country")}</p>
              <p className="text-sm font-medium">{transaction.country || fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("currency")}</p>
              <p className="text-sm font-medium">{effectiveCurrency || fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("processorBankcode")}</p>
              <p className="text-sm font-medium font-mono">{transaction.processorBankcode ?? transaction.processor_bankcode ?? fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("payoutType")}</p>
              <p className="text-sm font-medium capitalize">{(transaction.payoutType ?? transaction.payout_type)?.replace('_', ' ') || fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("platform")}</p>
              <p className="text-sm font-medium">{transaction.platformName || fallbackNA}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{translate("ipAddress")}</p>
              <p className="text-sm font-medium font-mono">{transaction.ipAddress ?? transaction.ip_address ?? fallbackNA}</p>
            </div>
          </div>

          {/* Customer Info */}
          {(transaction.customerName ?? transaction.customer_name ?? transaction.customerEmail ?? transaction.customer_email ?? transaction.customerPhone ?? transaction.customer_phone) && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">{translate("customerInfo")}</p>
              <div className="grid grid-cols-1 gap-2">
                {(transaction.customerName ?? transaction.customer_name) && (
                  <div className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500">{translate("name")}</span>
                    <span className="text-sm font-medium">{transaction.customerName ?? transaction.customer_name}</span>
                  </div>
                )}
                {(transaction.customerEmail ?? transaction.customer_email) && (
                  <div className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500">{translate("email")}</span>
                    <span className="text-sm font-medium">{transaction.customerEmail ?? transaction.customer_email}</span>
                  </div>
                )}
                {(transaction.customerPhone ?? transaction.customer_phone) && (
                  <div className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500">{translate("phone")}</span>
                    <span className="text-sm font-medium">{transaction.customerPhone ?? transaction.customer_phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Timeline */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">{translate("statusTimeline")}</p>
            <div className="space-y-4">
              {statusTimeline.map((step, index) => {
                const isLast = index === statusTimeline.length - 1;
                const isFailed = step.status === "failed" || step.status === "declined";

                return (
                  <div key={step.status} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done
                        ? isFailed
                          ? "bg-red-100"
                          : "bg-emerald-100"
                        : "bg-slate-100"
                        }`}>
                        {step.done ? (
                          isFailed ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          )
                        ) : (
                          <Clock className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 h-8 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <p className={`text-sm font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}>
                        {step.label}
                      </p>
                      {step.time && step.done && (
                        <p className="text-[11px] text-slate-500">
                          {formatDateSafe(step.time)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error Message */}
          {transaction.error_message && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-800">{translate("error")}</p>
              <p className="text-sm text-red-700 mt-1 break-words">{errorMessageText}</p>
            </div>
          )}

          {/* Callback Response (from qr_codes.callback_response / withdrawals.callback_response) */}
          {callbackMessageText && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <p className="text-sm font-medium text-slate-700">{translate("callbackResponse") || "Callback Response"}</p>
              <p className="text-sm text-slate-700 mt-1 break-words">{callbackMessageText}</p>
            </div>
          )}

          {/* Metadata Display */}
          {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{translate("metadata")}</p>
              <div className="grid grid-cols-1 gap-1">
                {Object.entries(transaction.metadata).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-slate-400 capitalize">{key}:</span>
                    <span className="text-xs font-medium text-slate-700">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footprints */}
          <div className="flex items-center justify-between text-[10px] text-slate-400 pt-4 border-t">
            <p>{t ? t("systemCreatedAt") || "Sys Created" : "Sys Created"}: {formatDateSafe(transaction.createdAt || transaction.created_date)}</p>
            <p>{t ? t("lastModified") || "Last Modified" : "Last Modified"}: {formatDateSafe(transaction.updatedAt)}</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
